import React, { FC, useState } from "react";
import styles from "./Admin.module.scss";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import apiInstance from "../services/httpService";

/** Simple, lint-clean email syntax check. */
function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.toLowerCase());
}

interface WhitelistUserProps {}

const WhitelistUser: FC<WhitelistUserProps> = () => {
  const [email, setEmail] = useState("");
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  const Alert = React.forwardRef(function Alert(props: any, ref: any) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
  });

  const trimmed = email.trim();
  const emailValid = isValidEmail(trimmed);
  const showSyntaxError = touched && trimmed.length > 0 && !emailValid;

  const toast = (message: string, severity: "success" | "error") =>
    setSnackbar({ open: true, message, severity });

  const submit = () => {
    if (!trimmed) {
      toast("Please enter an email id.", "error");
      return;
    }
    if (!emailValid) {
      setTouched(true);
      toast("Please enter a valid email id.", "error");
      return;
    }

    const token = localStorage.getItem("token");
    if (!token) {
      toast("You must be logged in.", "error");
      return;
    }

    setSubmitting(true);
    apiInstance
      .axiosCall("api/ccc/user/whitelist", { token, email: trimmed }, "POST")
      .then((response: any) => {
        setSubmitting(false);
        if (response?.data?.status === "success") {
          // Success: clear the field and show a success toast
          setEmail("");
          setTouched(false);
          toast(response.data.message || "User whitelisted successfully.", "success");
        } else {
          // Failure: keep the field, show the error toast
          toast(response?.data?.message || "Whitelisting failed. Please try again.", "error");
        }
      })
      .catch((err: any) => {
        setSubmitting(false);
        const detail =
          err?.response?.data?.message ||
          err?.message ||
          "Whitelisting failed. Please try again.";
        toast(detail, "error");
      });
  };

  return (
    <div className={styles.Admin__form}>
      <label className={styles.Admin__form_label}>
        Enter the email id of the user to whitelist
      </label>

      <TextField
        fullWidth
        label="Email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        onBlur={() => setTouched(true)}
        error={showSyntaxError}
        helperText={showSyntaxError ? "Please enter a valid email id." : " "}
        variant="outlined"
        size="small"
      />

      <Button
        variant="contained"
        onClick={submit}
        disabled={submitting}
        sx={{
          alignSelf: "flex-start",
          backgroundColor: "#3a8bac",
          "&:hover": { backgroundColor: "#195d7a" },
        }}
      >
        {submitting ? "Submitting..." : "Submit"}
      </Button>

      {/* Toast */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default WhitelistUser;
